import { formatXTick } from '@/pages/client-portal/ui/utils'
import '@/pages/dashboard/applicants/case-detail/ui/check-case/dialogs/visa-bulletin-trend-dialog/constants'
import { VisaBulletinTrendChartTooltip } from '@/pages/ui/chart-tooltips/visa-bulletin-trend-chart-tooltip/visa-bulletin-trend-chart-tooltip'
import {
  LineChart,
  LineChartActiveMarker,
  LineChartCurrentMarker,
  LineChartDataLine,
  LineChartTickLines,
  LineChartTooltip,
  LineChartXAxis,
  LineChartYAxis,
} from '@/pages/ui/line-chart'
import { LineChartDropLine } from '@/pages/ui/line-chart/line-chart-drop-line'
import { VisaBulletinTrendChartMyPriorityDate } from '@/pages/ui/visa-bulletin-trend-chart-my-priority-date/visa-bulletin-trend-chart-my-priority-date'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/card/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/tabs/tabs'

import { type ClientPortalVisaBulletinTrendItem } from '@/lib/queries/case-dashboard/types'
import { cn } from '@/lib/utils'

import {
  MIN_PLOT_AREA_WIDTH,
  PLOT_AREA_HEIGHT,
  PLOT_AREA_PADDING_RIGHT,
  PLOT_AREA_PADDING_TOP,
  VISA_BULLETIN_TREND_TIMEFRAME_OPTIONS,
  X_AXIS_HEIGHT,
  Y_AXIS_WIDTH,
} from './constants'
import { useVisaBulletinTrendCard } from './use-visa-bulletin-trend-card'

export interface VisaBulletinTrendCardProps {
  visaBulletinTrendData?: ClientPortalVisaBulletinTrendItem[]
  priorityDate?: string
}

export const VisaBulletinTrendCard = ({ visaBulletinTrendData, priorityDate }: VisaBulletinTrendCardProps) => {
  const { monthlyVisaBulletinTrendData, currentTimeFrame, handleChangeTimeFrame } = useVisaBulletinTrendCard({
    visaBulletinTrendData,
  })

  return (
    <Card className={cn('min-w-0')}>
      <CardHeader>
        <CardTitle className={cn('text-lg font-semibold text-black')}>Visa Bulletin Trend</CardTitle>
      </CardHeader>

      <CardContent className={cn('flex flex-col-reverse gap-y-6 lg:flex-col')}>
        <Tabs value={currentTimeFrame} onValueChange={handleChangeTimeFrame}>
          <TabsList className={cn('w-full lg:w-fit')}>
            {VISA_BULLETIN_TREND_TIMEFRAME_OPTIONS.map(({ label, value }) => (
              <TabsTrigger key={value} value={String(value)} className={cn('w-full')}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Card>
          <CardContent className={cn('pb-9 pl-9 pr-6 pt-9')}>
            {monthlyVisaBulletinTrendData && (
              <LineChart
                data={monthlyVisaBulletinTrendData}
                dataKeys={['filingDateDecimalYear', 'finalActionDateDecimalYear']}
                minPlotAreaWidth={MIN_PLOT_AREA_WIDTH}
                plotAreaHeight={PLOT_AREA_HEIGHT}
                plotAreaPadding={{
                  right: PLOT_AREA_PADDING_RIGHT,
                  top: PLOT_AREA_PADDING_TOP,
                }}
                xAxisHeight={X_AXIS_HEIGHT}
                yAxisWidth={Y_AXIS_WIDTH}
              >
                <LineChartXAxis dataKey="date" fontSize={10} tickFormatter={formatXTick} />
                <LineChartYAxis title="Date" />
                <LineChartTickLines />
                <LineChartDataLine dataKey="filingDateDecimalYear" noGradient strokeWidth={2} />
                <LineChartDataLine
                  dataKey="finalActionDateDecimalYear"
                  noGradient
                  strokeWidth={2}
                  color="hsla(21, 90%, 48%, 1)"
                />
                <LineChartCurrentMarker
                  dataKey="filingDateDecimalYear"
                  render={(point) => (
                    <>
                      <circle cx={point.x} cy={point.y} r="5.5" fill="white" />
                      <circle cx={point.x} cy={point.y} r="4.5" fill="hsla(228, 55%, 42%, 1)" />
                    </>
                  )}
                />

                <LineChartDropLine dataKeys={['filingDateDecimalYear', 'finalActionDateDecimalYear']} />

                <VisaBulletinTrendChartMyPriorityDate priorityDate={priorityDate} />

                <LineChartCurrentMarker
                  dataKey="finalActionDateDecimalYear"
                  render={(point) => (
                    <>
                      <circle cx={point.x} cy={point.y} r="5.0" fill="white" />
                      <circle cx={point.x} cy={point.y} r="4.0" fill="hsla(21, 90%, 48%, 1)" />
                    </>
                  )}
                />
                <LineChartActiveMarker
                  dataKey="filingDateDecimalYear"
                  noDropLine
                  render={() => (
                    <>
                      <circle r="5.5" fill="white" />
                      <circle r="4.5" fill="hsla(228, 55%, 42%, 1)" />
                    </>
                  )}
                />
                <LineChartActiveMarker
                  dataKey="finalActionDateDecimalYear"
                  noDropLine
                  render={() => (
                    <>
                      <circle r="5.5" fill="white" />
                      <circle r="4.5" fill="hsla(21, 90%, 48%, 1)" />
                    </>
                  )}
                />
                <LineChartTooltip
                  dataKeys={['filingDateDecimalYear', 'finalActionDateDecimalYear']}
                  render={() => <VisaBulletinTrendChartTooltip />}
                />
              </LineChart>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
